import { useMemo } from "react";
import { MaterialReactTable, createMRTColumnHelper, useMaterialReactTable } from "material-react-table";
import { useTheme } from '@mui/material/styles';
import { Box, Button, Typography, Tooltip, IconButton, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { Refresh, GppGood } from "@mui/icons-material";
import dayjs from 'dayjs';
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { notifyError } from "../../../components/common/toast/ToastFunc";

const columnHelper = createMRTColumnHelper();

const columns = [
    columnHelper.accessor("id", {
        header: "Transaction ID",
        size: 150,
    }),
    columnHelper.accessor("from_acc", {
        header: "From Acc",
        size: 150,
    }),
    columnHelper.accessor("to_acc", {
        header: "To Acc",
        size: 150,
    }),
    columnHelper.accessor("status", {
        header: "Status",
        size: 50,
    }),
    columnHelper.accessor("amount", {
        header: "Amount",
        size: 50,
    }),
    columnHelper.accessor("created_at", {
        header: "Time",
        size: 100,
    }),
    columnHelper.accessor("type", {
        header: "Type",
        size: 100,
    }),
];

const Table = (props) => {
    const { data, loading, actionLoading, setReFetch, transactionType, setTransactionType, authorizeTransaction } = props;

    const theme = useTheme();

    const buttonTheme = useMemo(() => {
        return {
            color: theme.mainColor, fontSize: 13, fontWeight: "bold", borderRadius: 2, borderColor: theme.mainColor, height: "45px",
            '&:hover': {
                backgroundColor: theme.mainColor, color: 'white', fontSize: 13, fontWeight: "bold", borderRadius: 2, borderColor: theme.mainColor
            },
        }
    }, [theme.mainColor]);

    const selectTheme = useMemo(() => ({
        '& .MuiInputLabel-root': {
            color: 'grey',
            '&.Mui-focused': {
                color: theme.mainColor,
            },
        },
        '& .MuiOutlinedInput-root': {
            width: '120px',
            height: '45px',
            '& fieldset': {
                borderColor: 'grey',
                borderRadius: '8px',
            },
            '&:hover fieldset': {
                borderColor: theme.mainColor,
            },
            '&.Mui-focused fieldset': {
                borderColor: theme.mainColor,
            },
        },
    }), [theme.mainColor]);

    const types = useMemo(() => {
        return ['All', 'Review', 'Success', 'Reject'];
    }, []);

    const handleExportRows = (rows) => {
        try {

            const doc = new jsPDF();
            const tableData = rows.map((row, index) => [
                index + 1,
                row.original["id"],
                row.original["status"],
                row.original["from_acc"],
                row.original["to_acc"],
                row.original["amount"],
                dayjs(row.original["created_at"]).format('MM/DD/YYYY HH:mm:ss'),
                row.original["type"],
            ]);

            const tableHeaders = ["No.", "Transaction ID", "Status", "From Account", "To Account", "Amount", "Date & Time", "Type"];

            autoTable(doc, {
                head: [tableHeaders],
                body: tableData,
            });

            doc.save("transaction_history_list.pdf");
        } catch (error) {
            notifyError("Something went wrong");
        }
    };

    const table = useMaterialReactTable({
        columns,
        data,
        state: { showLoadingOverlay: actionLoading || loading },
        initialState: { columnVisibility: { id: false } },
        muiCircularProgressProps: {
            color: "primary",
            thickness: 5,
            size: 55,
        },
        muiSkeletonProps: {
            animation: "pulse",
            height: 28,
        },
        enableFullScreenToggle: false,
        paginationDisplayMode: "pages",
        enableRowSelection: true,
        enableRowActions: true,
        muiPaginationProps: {
            rowsPerPageOptions: [5, 10, 15, 20, 30],
        },
        positionToolbarAlertBanner: "bottom",
        muiTableHeadCellProps: {
            sx: {
                fontSize: "18px",
            },
        },
        muiTableBodyCellProps: ({ row }) => ({
            sx: {
                fontSize: "15px",
                fontWeight: row.getIsSelected() ? "bold" : "normal",
            },
        }),

        renderRowActions: ({ row }) => (
            <Tooltip title='Authorize Transaction' placement="left">
                <MenuItem key="authorize" onClick={() => authorizeTransaction(row.original.id)} disabled={row.original?.is_authorized || loading || actionLoading}>
                    <GppGood sx={{ color: theme.mainColor, mr: 1 }} />
                    <Typography sx={{ color: theme.mainColor }}>{row.original?.is_authorized ? "Authorized" : "Authorize"}</Typography>
                </MenuItem>
            </Tooltip>
        ),

        renderTopToolbarCustomActions: ({ table }) => (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "15px",
                    padding: "5px",
                    flexWrap: "wrap",
                }}
            >
                <Tooltip arrow title='Refresh Data'>
                    <span>
                        <IconButton
                            disabled={loading || actionLoading}
                            onClick={() => setReFetch((prev) => !prev)}
                            sx={{ color: theme.mainColor }}
                        >
                            <Refresh />
                        </IconButton>
                    </span>
                </Tooltip>
                <FormControl sx={{ ...selectTheme }}>
                    <InputLabel>Trans Type</InputLabel>
                    <Select
                        label="Trans Type"
                        name="transactionType"
                        value={transactionType}
                        onChange={(event) => setTransactionType(event.target.value)}
                        disabled={loading || actionLoading}
                    >
                        {types.map((type, index) => (
                            <MenuItem key={index} value={type}>{type}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button
                    disabled={table.getPrePaginationRowModel().rows.length === 0 || loading || actionLoading}
                    onClick={() => handleExportRows(table.getPrePaginationRowModel().rows)}
                    startIcon={<FileDownloadIcon />}
                    variant='outlined'
                    sx={{ ...buttonTheme }}
                >
                    Export All
                </Button>
                <Button
                    disabled={(!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()) || loading || actionLoading}
                    onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
                    startIcon={<FileDownloadIcon />}
                    variant='outlined'
                    sx={{ ...buttonTheme }}
                >
                    Export Sel
                </Button>
            </Box >
        ),
    });

    return <MaterialReactTable table={table} />;
};

export default Table;
